import React, { useState, useEffect } from 'react';
import { useGame } from '../state/gameStore.tsx';
import { api } from '../api/client.ts';
import type { AllianceDetail, AllianceSummary, AllianceRole } from '@galaxy/shared';
import { ALLIANCE_CONFIG } from '@galaxy/shared';
import { CreditsIcon } from '../assets/icons.tsx';

export const AllianceView: React.FC = () => {
  const { state, refreshState } = useGame();
  const [alliance, setAlliance] = useState<AllianceDetail | null>(null);
  const [leaderboard, setLeaderboard] = useState<AllianceSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Form states for creation
  const [createName, setCreateName] = useState('');
  const [createTag, setCreateTag] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sub-tab when not in alliance
  const [nonMemberTab, setNonMemberTab] = useState<'create' | 'list'>('list');

  const loadData = async () => {
    try {
      setLoading(true);
      const [myRes, leadRes] = await Promise.all([
        api.getMyAlliance(),
        api.getAllianceLeaderboard(),
      ]);
      setAlliance(myRes.alliance);
      setLeaderboard(leadRes.leaderboard);
    } catch (err: any) {
      setErrorMsg(err.message || 'Lỗi tải dữ liệu liên minh');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = async () => {
    setErrorMsg(null);
    setSuccessMsg(null);
    if (!createName.trim() || !createTag.trim()) {
      setErrorMsg('Vui lòng nhập đầy đủ tên và viết tắt (Tag)!');
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await api.createAlliance({
        name: createName.trim(),
        tag: createTag.trim().toUpperCase(),
        description: createDesc.trim(),
      });
      setAlliance(res.alliance);
      setSuccessMsg(`Đã thành lập Liên minh [${res.alliance.tag}] thành công!`);
      await refreshState();
      await loadData();
    } catch (err: any) {
      setErrorMsg(err.message || 'Lỗi thành lập liên minh');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleJoin = async (allianceId: string) => {
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      setIsSubmitting(true);
      const res = await api.joinAlliance(allianceId);
      setAlliance(res.alliance);
      setSuccessMsg(`Đã gia nhập Liên minh [${res.alliance.tag}] thành công!`);
      await refreshState();
      await loadData();
    } catch (err: any) {
      setErrorMsg(err.message || 'Lỗi gia nhập liên minh');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDonate = async (amount: number) => {
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      setIsSubmitting(true);
      const res = await api.donateAlliance(amount);
      setAlliance(res.alliance);
      setSuccessMsg(`Đã quyên góp ${amount.toLocaleString()} Credits cho liên minh!`);
      await refreshState();
      await loadData();
    } catch (err: any) {
      setErrorMsg(err.message || 'Lỗi quyên góp');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('Bạn có chắc chắn muốn rời khỏi liên minh không?')) return;
    setErrorMsg(null);
    try {
      setIsSubmitting(true);
      await api.leaveAlliance();
      setAlliance(null);
      setSuccessMsg('Đã rời khỏi liên minh.');
      await refreshState();
      await loadData();
    } catch (err: any) {
      setErrorMsg(err.message || 'Lỗi rời liên minh');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisband = async () => {
    if (!window.confirm('CẢNH BÁO: Bạn có chắc chắn muốn GIẢI TÁN liên minh vĩnh viễn?')) return;
    setErrorMsg(null);
    try {
      setIsSubmitting(true);
      await api.disbandAlliance();
      setAlliance(null);
      setSuccessMsg('Liên minh đã được giải tán thành công.');
      await refreshState();
      await loadData();
    } catch (err: any) {
      setErrorMsg(err.message || 'Lỗi giải tán liên minh');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKick = async (targetId: string, username: string) => {
    if (!window.confirm(`Bạn có chắc muốn khai trừ ${username} khỏi liên minh?`)) return;
    try {
      setIsSubmitting(true);
      const res = await api.kickAllianceMember(targetId);
      setAlliance(res.alliance);
      setSuccessMsg(`Đã khai trừ ${username} thành công.`);
      await loadData();
    } catch (err: any) {
      setErrorMsg(err.message || 'Lỗi trục xuất thành viên');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePromote = async (targetId: string, role: AllianceRole) => {
    try {
      setIsSubmitting(true);
      const res = await api.promoteAllianceMember(targetId, role);
      setAlliance(res.alliance);
      setSuccessMsg('Đã cập nhật chức vụ thành viên.');
      await loadData();
    } catch (err: any) {
      setErrorMsg(err.message || 'Lỗi bổ nhiệm thành viên');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading && !alliance) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim)' }}>
        Đang đồng bộ dữ liệu liên minh thiên hà...
      </div>
    );
  }

  return (
    <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Title */}
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-bright)', letterSpacing: 0.5 }}>
          TRUNG TÂM LIÊN MINH THIÊN HÀ
        </h2>
        <p style={{ fontSize: 12, color: 'var(--text-dim)' }}>
          Hợp sức cùng các chỉ huy khám phá vũ trụ, phát triển công nghệ chung và kiểm soát lãnh thổ
        </p>
      </div>

      {/* Messages */}
      {errorMsg && (
        <div className="sci-card" style={{ borderColor: 'var(--status-red)', background: 'rgba(255, 23, 68, 0.1)', color: 'var(--status-red)', fontSize: 12 }}>
          ⚠️ {errorMsg}
        </div>
      )}
      {successMsg && (
        <div className="sci-card" style={{ borderColor: 'var(--status-green)', background: 'rgba(0, 230, 118, 0.1)', color: 'var(--status-green)', fontSize: 12 }}>
          ✓ {successMsg}
        </div>
      )}

      {/* VIEW 1: PLAYER IN ALLIANCE */}
      {alliance ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Alliance Header Card */}
          <div className="sci-card" style={{ background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.05), rgba(10, 20, 35, 0.95))', borderColor: 'var(--accent-cyan)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* SVG Crest */}
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'rgba(0, 229, 255, 0.15)',
                  border: '1.5px solid var(--accent-cyan)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent-cyan)" strokeWidth="2">
                    <path d="M12 2L3 7v6c0 5.5 3.8 10.7 9 12 5.2-1.3 9-6.5 9-12V7l-9-5z" />
                    <circle cx="12" cy="12" r="3" fill="var(--accent-cyan)" />
                  </svg>
                </div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-bright)' }}>
                      [{alliance.tag}] {alliance.name}
                    </span>
                    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 2, background: 'rgba(0, 229, 255, 0.2)', color: 'var(--accent-cyan)', fontWeight: 700 }}>
                      CẤP {alliance.level}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>
                    Minh Chủ: <strong style={{ color: 'var(--accent-yellow)' }}>{alliance.leader_name}</strong> | Thành viên: <strong>{alliance.members_count}/{alliance.max_members}</strong>
                  </div>
                </div>
              </div>

              {/* My Role Badge */}
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>Chức vụ của bạn:</div>
                <div style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: alliance.my_role === 'leader' ? 'var(--accent-yellow)' : alliance.my_role === 'officer' ? 'var(--accent-cyan)' : 'var(--text-bright)',
                }}>
                  {alliance.my_role === 'leader' ? '👑 Minh Chủ' : alliance.my_role === 'officer' ? '⚔️ Phó Minh Chủ' : '🛡️ Thành Viên'}
                </div>
              </div>
            </div>

            {alliance.description && (
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 8, fontStyle: 'italic', borderTop: '1px solid var(--border-panel)', paddingTop: 6 }}>
                "{alliance.description}"
              </div>
            )}

            {/* Active Buffs Banner */}
            <div style={{
              marginTop: 10,
              padding: '8px 10px',
              borderRadius: 4,
              background: 'rgba(0, 0, 0, 0.4)',
              border: '1px solid var(--border-panel)',
              display: 'flex',
              justifyContent: 'space-around',
              flexWrap: 'wrap',
              gap: 8,
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>⚡ Sản lượng mỏ</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--status-green)' }}>
                  +{alliance.buffs.resource_yield_percent}%
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>🚀 Tốc độ hạm đội</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-cyan)' }}>
                  +{alliance.buffs.fleet_speed_percent}%
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>📦 Sức chứa kho</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-yellow)' }}>
                  +{alliance.buffs.storage_capacity_percent}%
                </div>
              </div>
            </div>
          </div>

          {/* Alliance Tech & Donation Card */}
          <div className="sci-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-bright)' }}>
                🧬 CÔNG NGHỆ LIÊN MINH (CẤP {alliance.tech_level}/5)
              </div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--accent-yellow)' }}>
                Tổng quyên góp: {alliance.total_donated.toLocaleString()} Credits
              </div>
            </div>

            <p style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 10 }}>
              Đóng góp Credits để gia tăng cấp độ công nghệ, mang lại buff vĩnh viễn cho tất cả thành viên trong liên minh.
            </p>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <button
                className="sci-btn"
                disabled={isSubmitting || (state?.planet.resources.credits || 0) < 1000}
                onClick={() => handleDonate(1000)}
                style={{ flex: 1, padding: '6px 8px', fontSize: 11 }}
              >
                +1,000 <CreditsIcon size={12} />
              </button>
              <button
                className="sci-btn"
                disabled={isSubmitting || (state?.planet.resources.credits || 0) < 5000}
                onClick={() => handleDonate(5000)}
                style={{ flex: 1, padding: '6px 8px', fontSize: 11 }}
              >
                +5,000 <CreditsIcon size={12} />
              </button>
              <button
                className="sci-btn primary"
                disabled={isSubmitting || (state?.planet.resources.credits || 0) < 10000}
                onClick={() => handleDonate(10000)}
                style={{ flex: 1, padding: '6px 8px', fontSize: 11 }}
              >
                +10,000 <CreditsIcon size={12} />
              </button>
            </div>
          </div>

          {/* Members Roster Card */}
          <div className="sci-card">
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-bright)', marginBottom: 8 }}>
              👥 DANH SÁCH THÀNH VIÊN ({alliance.members.length}/{alliance.max_members})
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {alliance.members.map((m) => (
                <div
                  key={m.player_id}
                  style={{
                    background: 'var(--bg-panel)',
                    border: '1px solid var(--border-panel)',
                    padding: '8px 10px',
                    borderRadius: 4,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 6,
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{
                        fontSize: 10,
                        padding: '1px 5px',
                        borderRadius: 2,
                        background: m.role === 'leader' ? 'rgba(255, 179, 0, 0.2)' : m.role === 'officer' ? 'rgba(0, 229, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                        color: m.role === 'leader' ? 'var(--accent-yellow)' : m.role === 'officer' ? 'var(--accent-cyan)' : 'var(--text-dim)',
                        fontWeight: 700,
                      }}>
                        {m.role === 'leader' ? 'Minh Chủ' : m.role === 'officer' ? 'Phó Minh Chủ' : 'Thành Viên'}
                      </span>
                      <strong style={{ fontSize: 13, color: 'var(--text-bright)' }}>{m.username}</strong>
                    </div>
                    <div className="mono" style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>
                      Đã góp: {m.donated_credits.toLocaleString()} Credits | Tham gia: {new Date(m.joined_at).toLocaleDateString()}
                    </div>
                  </div>

                  {/* Actions for Leaders/Officers */}
                  {alliance.my_role === 'leader' && m.player_id !== alliance.leader_id && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      {m.role === 'member' ? (
                        <button
                          className="sci-btn"
                          disabled={isSubmitting}
                          onClick={() => handlePromote(m.player_id, 'officer')}
                          style={{ padding: '3px 6px', fontSize: 10 }}
                        >
                          Bổ nhiệm Phó
                        </button>
                      ) : (
                        <button
                          className="sci-btn"
                          disabled={isSubmitting}
                          onClick={() => handlePromote(m.player_id, 'member')}
                          style={{ padding: '3px 6px', fontSize: 10 }}
                        >
                          Hạ chức
                        </button>
                      )}
                      <button
                        className="sci-btn"
                        disabled={isSubmitting}
                        onClick={() => handleKick(m.player_id, m.username)}
                        style={{ padding: '3px 6px', fontSize: 10, borderColor: 'var(--status-red)', color: 'var(--status-red)' }}
                      >
                        Khai trừ
                      </button>
                    </div>
                  )}

                  {alliance.my_role === 'officer' && m.role === 'member' && (
                    <button
                      className="sci-btn"
                      disabled={isSubmitting}
                      onClick={() => handleKick(m.player_id, m.username)}
                      style={{ padding: '3px 6px', fontSize: 10, borderColor: 'var(--status-red)', color: 'var(--status-red)' }}
                    >
                      Khai trừ
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Activity Logs Card */}
          <div className="sci-card">
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-bright)', marginBottom: 8 }}>
              📜 NHẬT KÝ HOẠT ĐỘNG
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}>
              {alliance.logs.map((log) => (
                <div key={log.id} style={{ fontSize: 11, color: 'var(--text-dim)', padding: '4px 6px', background: 'rgba(0, 0, 0, 0.2)', borderRadius: 2 }}>
                  <span className="mono" style={{ color: 'var(--accent-cyan)', marginRight: 6 }}>
                    [{new Date(log.created_at).toLocaleTimeString()}]
                  </span>
                  {log.message}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Actions */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="sci-btn"
              disabled={isSubmitting}
              onClick={handleLeave}
              style={{ flex: 1, padding: '8px', fontSize: 12, borderColor: 'var(--status-red)', color: 'var(--status-red)' }}
            >
              RỜI LIÊN MINH
            </button>
            {alliance.my_role === 'leader' && (
              <button
                className="sci-btn"
                disabled={isSubmitting}
                onClick={handleDisband}
                style={{ flex: 1, padding: '8px', fontSize: 12, background: 'rgba(255, 23, 68, 0.2)', borderColor: 'var(--status-red)', color: 'var(--status-red)' }}
              >
                GIẢI TÁN LIÊN MINH
              </button>
            )}
          </div>
        </div>
      ) : (
        /* VIEW 2: PLAYER NOT IN ALLIANCE */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Sub Navigation */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border-panel)' }}>
            <button
              onClick={() => setNonMemberTab('list')}
              style={{
                flex: 1,
                padding: '8px 12px',
                background: 'transparent',
                border: 'none',
                borderBottom: nonMemberTab === 'list' ? '2px solid var(--accent-cyan)' : '2px solid transparent',
                color: nonMemberTab === 'list' ? 'var(--accent-cyan)' : 'var(--text-dim)',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              DANH SÁCH & BẢNG XẾP HẠNG
            </button>
            <button
              onClick={() => setNonMemberTab('create')}
              style={{
                flex: 1,
                padding: '8px 12px',
                background: 'transparent',
                border: 'none',
                borderBottom: nonMemberTab === 'create' ? '2px solid var(--accent-cyan)' : '2px solid transparent',
                color: nonMemberTab === 'create' ? 'var(--accent-cyan)' : 'var(--text-dim)',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              TẠO LIÊN MINH MỚI
            </button>
          </div>

          {nonMemberTab === 'create' ? (
            <div className="sci-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-bright)' }}>
                🚩 THÀNH LẬP LIÊN MINH MỚI
              </div>
              <p style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                Chi phí sáng lập liên minh là <strong>{ALLIANCE_CONFIG.CREATE_COST_CREDITS.toLocaleString()} Credits</strong>. Bạn sẽ là Minh Chủ tối cao.
              </p>

              <div>
                <label style={{ fontSize: 11, color: 'var(--text-dim)' }}>Tên Liên Minh (3 - 30 ký tự):</label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="Ví dụ: Thiên Hà Tiên Phong"
                  className="sci-input"
                  style={{ width: '100%', marginTop: 4 }}
                />
              </div>

              <div>
                <label style={{ fontSize: 11, color: 'var(--text-dim)' }}>Viết Tắt / Tag (2 - 4 chữ in hoa):</label>
                <input
                  type="text"
                  value={createTag}
                  onChange={(e) => setCreateTag(e.target.value.toUpperCase())}
                  placeholder="Ví dụ: THTP"
                  maxLength={4}
                  className="sci-input"
                  style={{ width: '100%', marginTop: 4, textTransform: 'uppercase' }}
                />
              </div>

              <div>
                <label style={{ fontSize: 11, color: 'var(--text-dim)' }}>Tôn chỉ / Mô tả:</label>
                <textarea
                  value={createDesc}
                  onChange={(e) => setCreateDesc(e.target.value)}
                  placeholder="Mô tả tôn chỉ hoạt động..."
                  className="sci-input"
                  rows={3}
                  style={{ width: '100%', marginTop: 4 }}
                />
              </div>

              <button
                className="sci-btn primary"
                disabled={isSubmitting || (state?.planet.resources.credits || 0) < ALLIANCE_CONFIG.CREATE_COST_CREDITS}
                onClick={handleCreate}
                style={{ padding: '10px', marginTop: 6 }}
              >
                {isSubmitting ? 'ĐANG KHỞI TẠO...' : `SÁNG LẬP NGAY (${ALLIANCE_CONFIG.CREATE_COST_CREDITS.toLocaleString()} CREDITS)`}
              </button>
            </div>
          ) : (
            <div className="sci-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-bright)' }}>
                🏆 BẢNG XẾP HẠNG LIÊN MINH THIÊN HÀ
              </div>

              {leaderboard.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-dim)', fontSize: 12 }}>
                  Chưa có liên minh nào được thành lập. Hãy là người đầu tiên!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {leaderboard.map((a, idx) => (
                    <div
                      key={a.id}
                      style={{
                        background: 'var(--bg-panel)',
                        border: '1px solid var(--border-panel)',
                        padding: '8px 10px',
                        borderRadius: 4,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 8,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          background: idx === 0 ? 'var(--accent-yellow)' : idx === 1 ? 'var(--accent-cyan)' : 'rgba(255, 255, 255, 0.1)',
                          color: idx < 2 ? '#000' : 'var(--text-bright)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 800,
                        }}>
                          #{idx + 1}
                        </div>

                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <strong style={{ fontSize: 13, color: 'var(--text-bright)' }}>
                              [{a.tag}] {a.name}
                            </strong>
                            <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 2, background: 'rgba(0, 229, 255, 0.2)', color: 'var(--accent-cyan)' }}>
                              Cấp {a.level} | Tech {a.tech_level}
                            </span>
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>
                            Minh Chủ: {a.leader_name} | Thành viên: {a.members_count}/{a.max_members}
                          </div>
                        </div>
                      </div>

                      <button
                        className="sci-btn"
                        disabled={isSubmitting || a.members_count >= a.max_members}
                        onClick={() => handleJoin(a.id)}
                        style={{ padding: '4px 10px', fontSize: 11 }}
                      >
                        {a.members_count >= a.max_members ? 'ĐÃ ĐẦY' : 'GIA NHẬP'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
